// ServeOrderCard.jsx
import { Box, Typography, useTheme, Divider, Chip } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import CustomTypography from 'components/Fileds/CustomTypography';
import ElapsedTimer from './ElapsedTimer';
import { ReactComponent as ArrowLeft } from "assests/arrow_left.svg";
import { ReactComponent as ArrowRight } from "assests/arrow_right.svg";

export default function ServeOrderCard({ order, from, onArrowClick, validTransitions }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: order.id });
  const theme = useTheme();

  const hasPrev = Array.isArray(validTransitions[from]) && validTransitions[from].length > 1;
  const hasNext = Array.isArray(validTransitions[from]) && validTransitions[from].length > 0;

  return (
    <Box
      ref={setNodeRef}
      {...attributes} {...listeners}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid #BDBDBD',
        p: 1,
        position: 'relative',
        cursor: 'grab',
      }}
    >
      {/* header: order no + time */}
      <Box className='d-flex align-items-center justify-content-between'>
        <CustomTypography
          variant="caption"
          label={`#${order.order_no}`}
          sx={{ fontSize: '14px', fontWeight: '400', color: theme.palette.drawermenuitem.main, mb: 0 }}
        />
        {order?.status !== 'served' &&
          <CustomTypography
            variant="caption"
            label={
              <ElapsedTimer
                timestamp={order.statusTimestamp}
                warnAfter={16}
              />
            }
            sx={{ fontSize: '16px', fontWeight: '400', mb: 0 }}
          />}
      </Box>

      {/* item + qty */}
      <Box>
        <Box className='d-flex align-items-center justify-content-between'>
          <Box className='d-flex align-items-center' sx={{ gap: 0.5 }}>
            <CustomTypography
              variant="body2"
              label={`${order.item_name}`}
              sx={{ fontSize: '16px', fontWeight: '700', mb: 0 }}
            />
            <CustomTypography
              variant="body2"
              label={`x ${order.quantity}`}
              sx={{ fontSize: '16px', fontWeight: '600', color: theme.palette.drawermenuitem.main, mb: 0 }}
            />
          </Box>
          {order?.isMiscellaneous &&
            <Chip
              label="MISC"
              size="small"
              sx={{
                height: '18px',
                borderRadius: '4px',
                backgroundColor: '#000',
                color: '#fff',
                fontWeight: 700,
                fontSize: '10px',
                letterSpacing: '0.4px',
                '& .MuiChip-label': {
                  px: "6px !important",
                  py: 0,
                  lineHeight: 1.2,
                },
              }}
            />
          }
        </Box>

        {/* add‑ons list */}
        {order?.add_ons?.length > 0 && (
          <Box sx={{ mt: 1, mb: 0.5 }}>
            <CustomTypography
              variant={"body2"}
              label={"Add-Ons"}
              sx={{ fontSize: '14px', fontWeight: '400', textDecoration: 'underline', color: theme.palette.drawermenuitem.main, mb: 0.25 }}
            />
            {order?.add_ons?.map(name => (
              <Box key={name} className='d-flex align-items-center' sx={{ gap: 1, pl: 0.5, py: 0.1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.drawermenuitem.main, flexShrink: 0 }} />
                <CustomTypography variant="caption"
                  label={name}
                  sx={{ fontSize: '14px', fontWeight: '300', color: theme.palette.drawermenuitem.main, mb: 0 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* notes (if any) */}
        {order?.notes && (
          <Typography variant="caption" display="block" mb={0.5}>
            <strong>Notes:</strong> {order.notes}
          </Typography>
        )}

        <Divider sx={{ borderColor: '#D9D9D9', my: 1 }} />
        <Box className='d-flex align-items-start justify-content-between'>
          {/* table / type tag */}
          <CustomTypography
            variant={"body2"}
            label={order.table}
            sx={{ fontSize: '14px', fontWeight: '400', color: theme.palette.drawermenuitem.main, mb: 0, height: '15px' }}
          />
          <Box className='d-flex align-items-center' sx={{ gap: 1 }}>
            {/* {Object.entries(validTransitions)
                    .filter(([to, arr]) => arr.includes(from))
                    .length > 0 && ( */}
            {hasPrev && (
              <ArrowLeft className='cursor-pointer' onClick={() => onArrowClick(order.id, from, 'back')} />
            )}
            {/* forward */}
            {/* {validTransitions[from]?.length > 0 && ( */}
            {hasNext && (
              <ArrowRight className='cursor-pointer' onClick={() => onArrowClick(order.id, from, 'forward')} />
            )}
          </Box>

        </Box>
        <Box sx={{ mt: 1 }}>
          {order?.status == 'ready_to_prepare' ? (
            <Box className='d-flex align-items-center' sx={{ gap: 0.5 }}>
              <CustomTypography
                variant="body2"
                label={`Station: `}
                sx={{ fontSize: '14px', fontWeight: '400', color: theme.palette.drawermenuitem.main, mb: 0 }}
              />
              <CustomTypography
                variant="body2"
                label={order?.station?.name && order?.station?.name !== 'undefined' ? order?.station?.name : "No station assigned"}
                sx={{ fontSize: '14px', fontWeight: '500', color: order?.station?.name && order?.station?.name !== 'undefined' ? '#2E7D32' : '#888888', mb: 0 }}
              />
            </Box>
          ) : (
            <Box className='d-flex align-items-center' sx={{ gap: 0.5 }}>
              <CustomTypography
                variant="body2"
                label={`Chef: `}
                sx={{ fontSize: '14px', fontWeight: '400', color: theme.palette.drawermenuitem.main, mb: 0 }}
              />
              {(() => {
                const rawChefName = order?.chef?.full_name || order?.chef?.name || (typeof order?.chef === 'string' ? order?.chef : null);
                const chefName = rawChefName && rawChefName !== 'undefined' && rawChefName.trim() !== '' ? rawChefName : null;
                return (
                  <CustomTypography
                    variant="body2"
                    label={chefName || "No chef assigned"}
                    sx={{ fontSize: '14px', fontWeight: '500', color: chefName ? '#2E7D32' : '#888888', mb: 0 }}
                  />
                );
              })()}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
